import type { SystemStyleObject } from '@chakra-ui/system';

const defaultSmall = {
    width: '12px',
    height: '12px',
    padding: '2px',
};

const defaultMedium = {
    width: '16px',
    height: '16px',
    padding: '4px',
};

const defaultLarge = {
    width: '24px',
    height: '24px',
    padding: '4px',
};

const defaultSizes = {
    sm: defaultSmall,
    md: defaultMedium,
    lg: defaultLarge,
};

const hoverWithOutline: SystemStyleObject = {
    borderRadius: '50px',
    outlineColor: 'slt-pepper.300',
    outlineWidth: '1px',
    outlineStyle: 'solid',
};

const closeIcon: SystemStyleObject = {
    backgroundColor: 'white',
    borderRadius: '50px',
    borderColor: 'slt-pepper.300',
    borderStyle: 'solid',
    borderWidth: '1px',
};

const closeIconHover: SystemStyleObject = { backgroundColor: 'slt-grey.100' };

export const IconMap: Record<string, Record<string, SystemStyleObject>> = {
    'expand-arrow-circle': {
        sm: { width: '24px', height: '24px', padding: '0' },
        md: { width: '32px', height: '32px', padding: '0' },
        lg: { width: '40px', height: '40px', padding: '0' },
    },
    'magnifying-glass-zoom-out': defaultSizes,
    'magnifying-glass-zoom-in': defaultSizes,
    'play-in-circle': { ...defaultSizes, xl: { width: '92px', height: '92px', padding: '4px' } },
    'light-play': { sm: { width: '10px', height: '12px', padding: '2px 3px' } },
    'dark-play': { sm: { width: '10px', height: '12px', padding: '2px 3px' } },
    play: { sm: { width: '10px', height: '12px', padding: '2px 3px' } },
    print: {
        sm: { width: '16px', height: '16px', padding: '4px' },
        md: { width: '20px', height: '20px', padding: '4px' },
        lg: { width: '24px', height: '24px', padding: '4px' },
    },
    'light-filled-circle': { sm: defaultSmall },
    'dark-filled-circle': { sm: defaultSmall },
    'navigation-arrow-left': {
        sm: {
            width: '6px',
            height: '12px',
            padding: '6px 9px',
            _hover: hoverWithOutline,
        },
        md: {
            width: '8px',
            height: '16px',
            padding: '8px 12px',
            _hover: hoverWithOutline,
        },
        lg: {
            width: '12px',
            height: '24px',
            padding: '8px 14px',
            _hover: hoverWithOutline,
        },
    },
    'navigation-arrow-right': {
        sm: {
            width: '6px',
            height: '12px',
            padding: '0px 9px',
            margin: '2px',
            _hover: hoverWithOutline,
        },
        md: {
            width: '8px',
            height: '16px',
            padding: '8px 12px',
            margin: '2px',
            _hover: hoverWithOutline,
        },
        lg: {
            width: '12px',
            height: '24px',
            padding: '8px 14px',
            margin: '2px',
            _hover: hoverWithOutline,
        },
    },
    'navigation-slide-arrow-left': {
        sm: { width: '24px', height: '24px', direction: 'left' },
        md: { width: '32px', height: '32px', direction: 'left' },
        lg: { width: '40px', height: '40px', direction: 'left' },
    },
    'navigation-slide-arrow-right': {
        sm: { width: '24px', height: '24px', direction: 'right' },
        md: { width: '32px', height: '32px', direction: 'right' },
        lg: { width: '40px', height: '40px', direction: 'right' },
    },
    'dropdown-arrow-open': {
        sm: { width: '12px', height: '6px', padding: '5px 2px' },
        md: { width: '16px', height: '8px', padding: '8px 4px' },
        lg: { width: '24px', height: '12px', padding: '10px 4px' },
    },
    'dropdown-arrow-close': {
        sm: { width: '12px', height: '6px', padding: '5px 2px' },
        md: { width: '16px', height: '8px', padding: '8px 4px' },
        lg: { width: '24px', height: '12px', padding: '10px 4px' },
    },
    close: {
        sm: {
            ...closeIcon,
            width: '12px',
            height: '12px',
            padding: '4px',
            _hover: closeIconHover,
        },
        md: {
            ...closeIcon,
            borderWidth: '1.8px',
            width: '16px',
            height: '16px',
            padding: '6px',
            _hover: closeIconHover,
        },
        lg: {
            ...closeIcon,
            borderWidth: '2px',
            width: '24px',
            height: '24px',
            padding: '6px',
            _hover: closeIconHover,
        },
    },
    shipping: defaultSizes,
    'in-store-pickup': {
        sm: { width: '10px', height: '12px', padding: '2px 3px' },
        md: { width: '12px', height: '16px', padding: '4px 6px' },
        lg: { width: '18px', height: '24px', padding: '4px 6px' },
    },
    'same-day-delivery': {
        sm: { width: '12px', height: '8px', padding: '4px 2px' },
        md: { width: '16px', height: '12px', padding: '6px 4px' },
        lg: { width: '24px', height: '16px', padding: '8px 4px' },
    },
    plus: defaultSizes,
    minus: {
        sm: { width: '12px', height: '2px', padding: '7px 2px' },
        md: { width: '16px', height: '2px', padding: '11px 4px' },
        lg: { width: '24px', height: '2px', padding: '15px 4px' },
    },
    'loading-wheel': defaultSizes,
    'use-my-location': defaultSizes,
    info: defaultSizes,
    'add-to-registry': {
        sm: { ...defaultSmall },
        md: { width: '24px', height: '24px', padding: '4px' },
        lg: { ...defaultLarge },
    },
    'shopping-cart-circle': {
        sm: { width: '24px', height: '24px', padding: '0' },
        md: { width: '32px', height: '32px', padding: '0' },
        lg: { width: '40px', height: '40px', padding: '0' },
    },
    'plus-circle': {
        sm: { width: '24px', height: '24px', padding: '0' },
        md: { width: '32px', height: '32px', padding: '0' },
        lg: { width: '40px', height: '40px', padding: '0' },
    },
    'add-to-bag': {
        sm: { width: '40px', height: '40px', padding: '0' },
        md: { width: '40px', height: '40px', padding: '0' },
        lg: { width: '40px', height: '40px', padding: '0' },
    },
    warning: defaultSizes,
    check: {
        sm: { width: '12px', height: '8px', padding: '4px 2px' },
        md: { width: '16px', height: '12px', padding: '6px 4px' },
        lg: { width: '24px', height: '16px', padding: '8px 4px' },
    },
    'copy-link': defaultSizes,
    snail: { sm: { width: '12px', height: '12px', padding: '2px' } },
    like: { sm: { width: '16px', height: '14px' } },
    'like-filled': { sm: { width: '16px', height: '14px' } },
    'reverted-like': { sm: { width: '16px', height: '14px' } },
    'reverted-like-filled': { sm: { width: '16px', height: '14px' } },
    dislike: { sm: { width: '16px', height: '14px' } },
    flag: { sm: { width: '14px', height: '16px' } },
    'flag-filled': { sm: { width: '14px', height: '16px' } },
    voteup: { sm: { width: '20px', height: '14px' } },
    chat: { sm: { width: '17px', height: '16px', padding: '4px' } },
    pencil: { sm: { width: '16px', height: '16px' } },
    star: {
        sm: { width: '16px', height: '16px' },
        md: { width: '24px', height: '24px' },
        lg: { width: '35px', height: '35px' },
    },
    'empty-star': {
        sm: { width: '16px', height: '16px' },
        md: { width: '24px', height: '24px' },
        lg: { width: '35px', height: '35px' },
    },
    'half-empty-star': {
        sm: { width: '16px', height: '16px' },
        md: { width: '24px', height: '24px' },
        lg: { width: '35px', height: '35px' },
    },
    'rounded-star': { lg: { width: '45px', height: '45px', padding: '4px' } },
    'empty-rounded-star': { lg: { width: '45px', height: '45px', padding: '4px' } },
    photo: { sm: { width: '16px', height: '16px' } },
    video: { sm: { width: '16px', height: '16px' } },
    search: { sm: { width: '16px', height: '16px' } },
    'progress-bar': { lg: { width: '32px', height: '32px' } },
    union: { sm: { width: '14px', height: '16px', padding: '4px 5px' } },
    gift: {
        sm: { width: '16px', height: '16px', padding: '8px' },
        md: { width: '24px', height: '24px' },
        lg: { width: '35px', height: '35px' },
    },
    tag: defaultSizes,
    'shopping-tag': defaultSizes,
    'ships-fast': {
        sm: { width: '16px', height: '16px' },
        md: { width: '24px', height: '24px' },
        lg: { width: '32px', height: '32px' },
    },
    store: {
        sm: { width: '24px', height: '24px' },
        md: { width: '32px', height: '32px' },
        lg: { width: '44px', height: '44px' },
    },
    'timer-cash': {
        sm: { width: '24px', height: '24px' },
        md: { width: '32px', height: '32px' },
        lg: { width: '44px', height: '44px' },
    },
    wallet: {
        sm: { width: '24px', height: '24px' },
        md: { width: '32px', height: '32px' },
        lg: { width: '44px', height: '44px' },
    },
    whisk: {
        sm: { width: '24px', height: '24px' },
        md: { width: '32px', height: '32px' },
        lg: { width: '44px', height: '44px' },
    },
    'external-link': {
        sm: { width: '24px', height: '24px' },
        md: { width: '32px', height: '32px' },
        lg: { width: '40px', height: '40px' },
    },
    filter: defaultSizes,
    email: defaultSizes,
    pinterest: defaultSizes,
    share: defaultSizes,
    wishlist: {
        sm: { width: '12px', height: '11px', padding: '2.5px 2px' },
        md: { width: '16px', height: '14px', padding: '5px 4px' },
        lg: { width: '24px', height: '22px', padding: '5px 4px' },
    },
    'wishlist-filled': {
        sm: { width: '12px', height: '11px', padding: '2.5px 2px' },
        md: { width: '16px', height: '14px', padding: '5px 4px' },
        lg: { width: '24px', height: '22px', padding: '5px 4px' },
    },
} as const;

export type IconName = keyof typeof IconMap;
